import {getCountryCode}     from "@Common/logic/template-options/get-country-code";
import {getMinQty}          from "@Common/logic/template-options/get-min-qty";
import {getPriceByQuantity} from "@Common/logic/template-options/get-price-by-quantity";
import {cs}                 from "@Common/react/chain-services";
import {consumeContext}     from "@Common/react/context";
import {fragments}          from "@Common/react/fragments";
import {joinJsx}            from "@Common/react/join-jsx";
import {Load2}              from "@Common/react/load2";
import {MLListTable}        from "@Common/ui-components/heavy/mailing-ui/ml-list-table/ml-list-table";
import {formatMoney1}       from "@Common/ui-components/language/format-money";
import {sum}                from "@Common/utils/collections";

import {AddressLists}              from "@Xpresso/routes/common/address-lists/address-lists";
import {getPurchaseMlSummaryLines} from "@Xpresso/routes/common/address-lists/get-purchase-ml-summary-lines";

import {ScDeliveryMethodLogic} from "../delivery-method/sc-delivery-method-logic";
import {ScAddressListsLogic}   from "../mailing/sc-address-lists-logic";

import {CommonShippingModules} from "./common-shipping-modules";
import {enrollmentEditor, ui}  from "./expo-types";

export const CommonMethodModules = ({initEnrollment, templateOptions, program, agentId, branchId}) => [
    {
        name: "getMailingPaperPrices",
        load: ({mailingClassId}, next) => (
            next((templateOptions) => (
                getPaperType({method: "mailing", templateOptions})?.productPrice?.["mailing"]?.[mailingClassId]
            ))
        ),
        requires: ["mailingClassId"],
    },
    {
        name: "mailingPaperPrices",
        load: ({getMailingPaperPrices}, next) => next(getMailingPaperPrices(templateOptions)),
        requires: ["getMailingPaperPrices"],
    },
    {
        name: "shippingPaperPrices",
        load: ({getShippingPaperPrices}, next) => next(getShippingPaperPrices(templateOptions)),
        requires: ["getShippingPaperPrices"],
    },
    {
        name: "getShippingPaperPrices",
        load: ({}, next) => next((templateOptions) => (
            getPaperType({method: "shipping", templateOptions})?.productPrice?.["shipping"]
        )),
        requires: [],
    },

    {
        name: "getMailingPaperPrice",
        load: ({getMailingPaperPrices, mailedQuantity}, next) => next(
            (templateOptions) => getPriceByQuantity(
                getMailingPaperPrices(templateOptions),
                mailedQuantity
            )
        ),
        requires: ["getMailingPaperPrices", "mailedQuantity"],
    },
    {
        name: "mailingPaperPrice",
        load: ({getMailingPaperPrice}, next) => next(
            getMailingPaperPrice(
                templateOptions
            )
        ),
        requires: ["getMailingPaperPrice"],
    },

    {
        name: "getShippingPaperPrice",
        load: ({getShippingPaperPrices, shippedQuantity}, next) => next(
            (templateOptions) => getPriceByQuantity(
                getShippingPaperPrices(templateOptions),
                shippedQuantity
            )
        ),
        requires: ["getShippingPaperPrices", "shippedQuantity"],
    },
    {
        name: "shippingPaperPrice",
        load: ({getShippingPaperPrice}, next) => next(getShippingPaperPrice(templateOptions)),
        requires: ["getShippingPaperPrice"],
    },

    {
        name: "methodLogic",
        load: ({}, next) => ScDeliveryMethodLogic({
            initEnrollment, templateOptions,
            program,
            next,
        }),
        expo: [enrollmentEditor, ui(0)],
    },
    {
        name: "method",
        load: ({methodLogic}, next) => next(methodLogic.method),
        requires: ["methodLogic"],
    },

    {
        name: "getPricePerPiece",
        load: ({method, getMailingPaperPrice, getShippingPaperPrice, unaddressedCopiesLogic}, next) => next((templateOptions) => {
            if (method === "mailing") {

                const mailingPaperPrice = getMailingPaperPrice(templateOptions);
                const shippingPaperPrice = getShippingPaperPrice(templateOptions);
                const hasShipping = !!unaddressedCopiesLogic.state.value;
                return joinJsx(
                    [
                        fragments(!mailingPaperPrice ? "--" : formatMoney1(mailingPaperPrice.price), hasShipping && ` (Mailed)`),
                        hasShipping && fragments(!shippingPaperPrice ? "--" : formatMoney1(shippingPaperPrice.price), hasShipping && ` (Shipped)`)
                    ].filter(v=>v),
                    () => " & ",
                );
            } else {
                const shippingPaperPrice = getShippingPaperPrice(templateOptions);
                return !shippingPaperPrice ? "--" : formatMoney1(shippingPaperPrice.price);
            }
        }),
        requires: ["method", "getMailingPaperPrice", "getShippingPaperPrice", "unaddressedCopiesLogic"],
    },


    {
        name: "mailedQuantity",
        load: ({method, addressLists1, sampleMailingCardLogic}, next) => next(
            method === "mailing" ? (
                (addressLists1?.quantity ?? 0) +
                (sampleMailingCardLogic?.selected ? 1 : 0) +
                0
            ) : (
                null
            )
        ),
        requires: ["method", "addressLists1", "sampleMailingCardLogic"],
    },
    {
        name: "shippedQuantity",
        load: ({shippingQuantity1, method, unaddressedCopiesQuantityLogic1, enableAddressableShippingLogic1, addressLists1}, next) => next(
            method === "mailing" ? (
                unaddressedCopiesQuantityLogic1?.quantity ??0
            ) : enableAddressableShippingLogic1?.state.value ? (
                addressLists1?.quantity??0
            ) : (
                shippingQuantity1?.quantity??0
            )
        ),
        requires: [
            "shippingQuantity1", "method", "unaddressedCopiesQuantityLogic1", "enableAddressableShippingLogic1",
            "addressLists1",
        ],
    },

    {
        name: "estimatedTotal",
        load: ({getEstimatedTotal}, next) => next(getEstimatedTotal(templateOptions)),
        requires: ["getEstimatedTotal"],
    },

    {
        name: "getEstimatedTotal",
        load: (
            {
                getShippingPaperPrice, getMailingPaperPrice, shippedQuantity, mailedQuantity, shippingServiceLevel1,
                addressLists1, mailingLists, method, enableAddressableShippingLogic1,
            },
            next
        ) => {

            return next((templateOptions) => {
                const shippingPaperPrice = getShippingPaperPrice(templateOptions);
                const mailingPaperPrice  = getMailingPaperPrice(templateOptions);

                // TODO + tax, waiting for Maggie to confirm
                return (
                    (
                        !shippingPaperPrice ? (
                            0
                        ) : shippingPaperPrice.flatPrice ? (
                            shippingPaperPrice.flatPrice
                        ) : (
                            (shippedQuantity ?? 0) * shippingPaperPrice.price
                        )
                    ) +
                    (
                        !mailingPaperPrice ? (
                            0
                        ) : mailingPaperPrice.flatPrice ? (
                            mailingPaperPrice.flatPrice
                        ) : (
                            (mailedQuantity ?? 0) * mailingPaperPrice.price
                        )
                    ) +
                    (!shippedQuantity ? 0 : +(shippingServiceLevel1.serviceLevelPrice ?? 0)) +
                    (
                        !templateOptions ? 0 : (
                            sum(
                                getPurchaseMlSummaryLines({
                                    templateOptions,
                                    mailingLists: addressLists1.state.value?.map((id) => mailingLists.value?.find((ml) => ml.id == id)).filter(v=>v),
                                    isMailing: method === "mailing",
                                    isAddressableShipping: enableAddressableShippingLogic1?.state.value,
                                }),
                                ({unitPrice, qty}) => unitPrice * qty,
                            )
                        )
                    ) +
                    0
                );
            });
        },
        requires: [
            "getShippingPaperPrice", "getMailingPaperPrice", "shippedQuantity", "mailedQuantity",
            "shippingServiceLevel1", "addressLists1", "mailingLists", "method", "enableAddressableShippingLogic1"
        ],
    },

    {
        name: "totalQuantity",
        load: ({shippedQuantity, mailedQuantity}, next) => next(
            (shippedQuantity??0) +
            (mailedQuantity??0) +
            0
        ),
        requires: ["shippedQuantity", "mailedQuantity"],
    },

    ...CommonMailingModules({initEnrollment, templateOptions, agentId, branchId}),
    ...CommonShippingModules({initEnrollment, templateOptions, agentId, branchId}),
];

const getPaperType = ({method, templateOptions}) => {
    const paperTypes = templateOptions?.options.filter((o) =>
        o.dev_key === "paper_type" && o.distributionFilter.includes(method)
    );
    return paperTypes?.find((o) => o.default) ?? paperTypes?.[0];
};



export const CommonMailingModules = ({initEnrollment, templateOptions, agentId, branchId}) => [


    {
        name: "addressLists1",
        load: ({mailingLists}, next) => ScAddressListsLogic({entityId: agentId || branchId || '', initEnrollment, mailingLists, next}),
        requires: ["mailingLists"],
    },


    {
        name: "addressLists2",
        load: ({addressLists1, mailedQuantity, shippedQuantity, mailingPaperPrice, shippingPaperPrice, method}, next) => addressLists1.next({
            ...method === "mailing" ? {
                totalQuantity: mailedQuantity,
                paperPrice: mailingPaperPrice,
            } : {
                totalQuantity: shippedQuantity,
                paperPrice: shippingPaperPrice,
            },
            next,
        }),
        requires: ["addressLists1", "mailedQuantity", "shippedQuantity", "mailingPaperPrice", "shippingPaperPrice", "method"],
    },
    {
        name: "mailingLists",
        load: ({}, next) => cs(
            consumeContext("apis"),
            ["mailingLists", ({apis}, next) => Load2({
                fetch: () => apis.next.mailing.getMailingLists({entityId: agentId}),
                next,
            })],
            ({mailingLists}) => next(mailingLists),
        ),
    },


    {
        name: "renderAddressLists",
        load: (
            {
                addressLists1, addressLists2, mailedQuantity, mailingLists, mailingPaperPrices,
                method, shippedQuantity, shippingPaperPrices,
            }, next
        ) => next(({showErrors, getExtraRows, className}) => cs(
            ["mailingTable", ({}, next) => MLListTable({
                agentId,
                branchId,
                // itemId: item.itemId,
                mailingListIds: addressLists1.state,
                mailingLists,
                showHeaderSummary: true,
                countryCode: getCountryCode({templateOptions}),
                eddmUnavailable: true,
                next,
            })],

            ({mailingTable}) => AddressLists({
                errorMessages: addressLists2?.ui?.errorMessages,
                ...method==="mailing" ? {
                    totalQuantity: mailedQuantity,
                    minQuantity: mailingPaperPrices && getMinQty(mailingPaperPrices),
                } : {
                    totalQuantity: shippedQuantity,
                    minQuantity: shippingPaperPrices && getMinQty(shippingPaperPrices),
                },
                getExtraRows,
                className,
                showErrors,
                mailingTable,
            }),
        )),
        requires: [
            "addressLists1", "addressLists2",
            "method",
            "mailedQuantity", "shippedQuantity",
            "mailingPaperPrices", "shippingPaperPrices",
            "mailingLists",
        ],
    },
];

